import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const BROKERS = ['Ben Rogers', 'Jesse Roach', 'Mitch Speer']
const BROKER_PHOTOS = {
  'Ben Rogers': 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0013_VSCO.jpg',
  'Jesse Roach': 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0061_VSCO.jpg',
  'Mitch Speer': 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0023_VSCO.jpg',
}
const MMS_SPLIT = 0.0695

function getMonday(d) {
  const date = new Date(d)
  date.setHours(0, 0, 0, 0)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  date.setDate(diff)
  return date
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtCurr(n) {
  if (!n || n <= 0) return '$0'
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function parseCurr(v) {
  return Number((v || '').toString().replace(/[^\d.]/g, '')) || 0
}

function calcNetRev(d) {
  const basePrem = parseCurr(d.base_premium)
  const commRate = Number(d.comm_rate || 0)
  const fee = parseCurr(d.broker_fee)
  const grossComm = basePrem * (commRate / 100)
  const grossIncome = grossComm + fee
  return (grossIncome - (grossIncome * MMS_SPLIT)) * 1.1
}

function weekLabel(monday) {
  const sun = new Date(monday)
  sun.setDate(sun.getDate() + 6)
  return `${fmtDate(monday)} – ${fmtDate(sun)}`
}

// ─── PRINT VIEW ───────────────────────────────────────────────────────────────
function printMeeting(data) {
  const { weekStart, scoreboard, leadsBySource,
      leadsByPartner, leadsByBroker, partnerStats, operations, commitments, prevCommitments, strategic, notes } = data

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Benchmark Weekly Meeting — ${weekLabel(weekStart)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; color: #1a1a1a; padding: 24px 32px; line-height: 1.5; }
  h1 { font-size: 18px; color: #1e3d30; margin-bottom: 4px; }
  h2 { font-size: 13px; color: #1e3d30; text-transform: uppercase; letter-spacing: .6px; border-bottom: 2px solid #1e3d30; padding-bottom: 4px; margin: 18px 0 8px; }
  h3 { font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: .4px; margin: 10px 0 4px; }
  .sub { font-size: 12px; color: #666; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  th, td { padding: 5px 8px; text-align: left; border: 1px solid #ddd; font-size: 11px; }
  th { background: #f4f7f5; font-weight: 700; color: #1e3d30; }
  .num { text-align: right; font-weight: 700; }
  .green { color: #1e9e75; }
  .red { color: #dc3545; }
  .amber { color: #7a5500; }
  .grid4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
  .stat { border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; }
  .stat-label { font-size: 9px; text-transform: uppercase; letter-spacing: .4px; color: #888; margin-bottom: 2px; }
  .stat-val { font-size: 18px; font-weight: 700; }
  .section-time { float: right; font-size: 10px; color: #999; font-weight: 400; }
  .commitments-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .broker-col { border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; }
  .broker-name { font-weight: 700; font-size: 12px; color: #1e3d30; margin-bottom: 6px; }
  .commit-item { padding: 3px 0; font-size: 11px; display: flex; gap: 4px; }
  .done { color: #1e9e75; }
  .notdone { color: #dc3545; }
  .notes-box { border: 1px solid #ddd; border-radius: 6px; padding: 10px; min-height: 60px; white-space: pre-wrap; }
  .empty-lines { border-bottom: 1px solid #eee; height: 22px; }
  @media print { body { padding: 12px 20px; } }
  .logo-line { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .page-break { page-break-before: always; }
</style></head><body>
<div class="logo-line">
  <div>
    <h1>Weekly Leadership Meeting</h1>
    <div class="sub">${weekLabel(weekStart)} · Benchmark Insurance Brokers</div>
  </div>
  <div style="font-size:10px;color:#888;">Printed ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
</div>

<h2>1. Scoreboard <span class="section-time">10 minutes</span></h2>
<div class="grid4">
  <div class="stat"><div class="stat-label">New Leads</div><div class="stat-val">${scoreboard.newLeads}</div></div>
  <div class="stat"><div class="stat-label">Quotes Sent</div><div class="stat-val">${scoreboard.quotesSent}</div></div>
  <div class="stat"><div class="stat-label">Policies Bound</div><div class="stat-val">${scoreboard.policiesBound}</div></div>
  <div class="stat"><div class="stat-label">Revenue This Week</div><div class="stat-val green">${fmtCurr(scoreboard.weekRevenue)}</div></div>
</div>
<div class="grid4">
  <div class="stat"><div class="stat-label">Active Leads</div><div class="stat-val">${scoreboard.activeLeads}</div></div>
  <div class="stat"><div class="stat-label">Outstanding Quotes</div><div class="stat-val ${scoreboard.outstandingQuotes > 5 ? 'amber' : ''}">${scoreboard.outstandingQuotes}</div></div>
  <div class="stat"><div class="stat-label">Aged Debtors</div><div class="stat-val ${scoreboard.agedDebtors > 0 ? 'red' : ''}">${scoreboard.agedDebtors}</div></div>
  <div class="stat"><div class="stat-label">Open Claims</div><div class="stat-val">${scoreboard.openClaims}</div></div>
</div>

<h3>Per Broker</h3>
<table>
  <tr><th>Broker</th><th class="num">Leads</th><th class="num">Quoted</th><th class="num">Bound</th><th class="num">Revenue</th></tr>
  ${BROKERS.map(b => {
    const d = leadsByBroker[b] || {}
    return `<tr><td>${b}</td><td class="num">${d.leads || 0}</td><td class="num">${d.quoted || 0}</td><td class="num">${d.bound || 0}</td><td class="num green">${fmtCurr(d.revenue || 0)}</td></tr>`
  }).join('')}
</table>

<h2>2. Lead Generation <span class="section-time">15 minutes</span></h2>
<table>
  <tr><th>Source</th><th class="num">Leads</th><th class="num">Quoted</th><th class="num">Bound</th><th class="num">Conversion</th></tr>
  ${Object.entries(leadsBySource).map(([source, d]) =>
    `<tr><td>${source}</td><td class="num">${d.total || 0}</td><td class="num">${d.quoted || 0}</td><td class="num">${d.bound || 0}</td><td class="num">${d.total > 0 ? Math.round((d.bound / d.total) * 100) : 0}%</td></tr>`
  ).join('')}
</table>
<div style="font-size:10px;color:#888;margin-bottom:4px;">Discussion: Which source is working? Which is wasting time? Where do we double down?</div>

<h2>3. Referral Partner Growth <span class="section-time">10 minutes</span></h2>
<div class="grid4">
  <div class="stat"><div class="stat-label">Active Partners</div><div class="stat-val">${partnerStats.active}</div></div>
  <div class="stat"><div class="stat-label">Referrals This Month</div><div class="stat-val">${partnerStats.thisMonth}</div></div>
  <div class="stat"><div class="stat-label">Referrals Converted</div><div class="stat-val green">${partnerStats.converted}</div></div>
  <div class="stat"><div class="stat-label">Partners Needing Follow-Up</div><div class="stat-val ${partnerStats.stale > 0 ? 'amber' : ''}">${partnerStats.stale}</div></div>
</div>
<div style="font-size:10px;color:#888;">Target: 1 new referral meeting per director per week · 1 new partner activated per month</div>

<h2>4. Operations & Service <span class="section-time">10 minutes</span></h2>
<table>
  <tr><th>Item</th><th class="num">Count</th><th>Action Needed</th></tr>
  <tr><td>Quotes older than 48 hours</td><td class="num ${operations.agedQuotes > 0 ? 'amber' : ''}">${operations.agedQuotes}</td><td>${operations.agedQuotes > 0 ? 'Follow up today' : 'All clear'}</td></tr>
  <tr><td>Open Claims</td><td class="num">${operations.openClaims}</td><td>${operations.openClaims > 0 ? 'Review status' : 'None'}</td></tr>
  <tr><td>Claims overdue update (10+ days)</td><td class="num ${operations.overdueClaims > 0 ? 'red' : ''}">${operations.overdueClaims}</td><td>${operations.overdueClaims > 0 ? 'Update client today' : 'All current'}</td></tr>
  <tr><td>BPAY Debtors (7+ days)</td><td class="num ${operations.urgentDebtors > 0 ? 'red' : ''}">${operations.urgentDebtors}</td><td>${operations.urgentDebtors > 0 ? 'Send follow-up SMS' : 'None'}</td></tr>
</table>
<div style="font-size:10px;color:#888;">Question: What is frustrating clients or slowing us down? Fix one thing this week.</div>

<h2>5. Strategic Discussion <span class="section-time">10 minutes — ONE topic only</span></h2>
<div style="margin-bottom:6px;"><strong>Topic:</strong> ${strategic.topic || '________________________________________'}</div>
<div style="margin-bottom:6px;"><strong>Decision:</strong> ${strategic.decision || '________________________________________'}</div>
<div class="empty-lines"></div><div class="empty-lines"></div>

<h2>6. Commitments <span class="section-time">5 minutes</span></h2>

${prevCommitments && prevCommitments.length > 0 ? `
<h3>Last Week's Commitments</h3>
<div class="commitments-grid">
  ${BROKERS.map(b => {
    const items = prevCommitments.filter(c => c.broker === b)
    return `<div class="broker-col"><div class="broker-name">${b.split(' ')[0]}</div>${items.length > 0 ? items.map(c => `<div class="commit-item"><span class="${c.done ? 'done' : 'notdone'}">${c.done ? '✅' : '❌'}</span> ${c.text}</div>`).join('') : '<div style="color:#888;font-style:italic;">None recorded</div>'}</div>`
  }).join('')}
</div>` : ''}

<h3>This Week's Commitments (3 each)</h3>
<div class="commitments-grid">
  ${BROKERS.map(b => {
    const items = commitments.filter(c => c.broker === b)
    return `<div class="broker-col"><div class="broker-name">${b.split(' ')[0]}</div>${items.length > 0 ? items.map(c => `<div class="commit-item">☐ ${c.text}</div>`).join('') : '<div class="empty-lines"></div><div class="empty-lines"></div><div class="empty-lines"></div>'}</div>`
  }).join('')}
</div>

<h2>Notes</h2>
<div class="notes-box">${notes || '<div class="empty-lines"></div><div class="empty-lines"></div><div class="empty-lines"></div><div class="empty-lines"></div>'}</div>

<div style="margin-top:20px;text-align:center;font-size:9px;color:#bbb;">Benchmark Insurance Brokers · AR No. 001320555 · Authorised Representative of McLardy McShane Partners Pty Ltd (AFSL 232987)</div>
</body></html>`

  const win = window.open('', '_blank')
  win.document.write(html)
  win.document.close()
  setTimeout(() => win.print(), 400)
}

// ─── MAIN MEETINGS COMPONENT ─────────────────────────────────────────────────
export function Meetings({ showToast, user, clients }) {
  const [weekOffset, setWeekOffset] = useState(0)
  const [meeting, setMeeting] = useState(null)
  const [prevMeeting, setPrevMeeting] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [coverConfirmations, setCoverConfirmations] = useState([])
  const [claims, setClaims] = useState([])
  const [referralPartners, setReferralPartners] = useState([])

  // Editable fields
  const [commitments, setCommitments] = useState([])
  const [strategicTopic, setStrategicTopic] = useState('')
  const [strategicDecision, setStrategicDecision] = useState('')
  const [notes, setNotes] = useState('')
  const [newCommitText, setNewCommitText] = useState({})
  const [statsRange, setStatsRange] = useState('week')
  const [drilldown, setDrilldown] = useState(null)

  const currentMonday = useMemo(() => {
    const m = getMonday(new Date())
    m.setDate(m.getDate() + weekOffset * 7)
    return m
  }, [weekOffset])

  const weekStartStr = currentMonday.toISOString().split('T')[0]
  const weekEnd = new Date(currentMonday)
  weekEnd.setDate(weekEnd.getDate() + 6)
  weekEnd.setHours(23, 59, 59, 999)

  const prevMonday = new Date(currentMonday)
  prevMonday.setDate(prevMonday.getDate() - 7)

  // ── Load all data ──
  const load = useCallback(async () => {
    setLoading(true)
    const [{ data: cc }, { data: cl }, { data: rp }, { data: mtg }, { data: prevMtg }] = await Promise.all([
      supabase.from('cover_confirmations').select('*').eq('status', 'Sent'),
      supabase.from('claims').select('*'),
      supabase.from('referral_partners').select('*'),
      supabase.from('weekly_meetings').select('*').eq('week_start', weekStartStr).maybeSingle(),
      supabase.from('weekly_meetings').select('*').eq('week_start', prevMonday.toISOString().split('T')[0]).maybeSingle(),
    ])
    setCoverConfirmations(cc || [])
    setClaims(cl || [])
    setReferralPartners(rp || [])
    setMeeting(mtg)
    setPrevMeeting(prevMtg)

    if (mtg) {
      setCommitments(mtg.commitments || [])
      setStrategicTopic(mtg.strategic_topic || '')
      setStrategicDecision(mtg.strategic_decision || '')
      setNotes(mtg.notes || '')
    } else {
      setCommitments([])
      setStrategicTopic('')
      setStrategicDecision('')
      setNotes('')
    }
    setLoading(false)
  }, [weekStartStr])

  useEffect(() => { load() }, [load])

  // ── Stats date range ──
  const statsDateRange = useMemo(() => {
    const now = new Date()
    if (statsRange === 'week') return { start: currentMonday, end: weekEnd }
    if (statsRange === 'month') return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now }
    if (statsRange === 'year') return { start: new Date(now.getFullYear(), 0, 1), end: now }
    return { start: new Date('2020-01-01'), end: now }
  }, [statsRange, currentMonday, weekEnd])

// ── Computed scoreboard data ──
  const scoreboard = useMemo(() => {
    const { start, end } = statsDateRange
    const inRange = (d) => { if (!d) return false; const dt = new Date(d); return dt >= start && dt <= end }
    const allLeads = (clients || []).filter(c => c.lead_status && c.lead_status !== 'Policy Placed' && c.lead_status !== 'Not Interested')
    const rangeLeads = (clients || []).filter(c => inRange(c.created_at) && c.lead_status)
    const quotesSent = allLeads.filter(c => c.lead_status === 'Quote Sent' || c.lead_status === 'Awaiting Client')
    const rangeBound = (coverConfirmations || []).filter(cc => inRange(cc.created_at))
    const rangeRevenue = rangeBound.reduce((s, cc) => s + calcNetRev(cc.cover_data || {}), 0)
    const debtors = (coverConfirmations || []).filter(cc => {
      const d = cc.cover_data || {}
      const pt = (d.payment_type || '').toLowerCase()
      const startDate = d.period_from ? new Date(d.period_from) : null
      if (!startDate) return false
      const daysOld = Math.floor((new Date() - startDate) / 86400000)
      return (pt !== 'monthly' && pt !== 'funded') && !d._remittance_paid && !d._paid_manually && daysOld >= 7
    })
    const openClaims = (claims || []).filter(c => !['Settled', 'Withdrawn', 'Declined'].includes(c.status))

    return {
      newLeads: rangeLeads.length,
      activeLeads: allLeads.length,
      quotesSent: quotesSent.length,
      outstandingQuotes: quotesSent.length,
      policiesBound: rangeBound.length,
      weekRevenue: rangeRevenue,
      agedDebtors: debtors.length,
      openClaims: openClaims.length,
    }
  }, [clients, coverConfirmations, claims, statsDateRange])

  // ── Drilldown lists ──
  const drilldownData = useMemo(() => {
    const { start, end } = statsDateRange
    const inRange = (d) => { if (!d) return false; const dt = new Date(d); return dt >= start && dt <= end }
    const allClients = clients || []
    return {
      newLeads: allClients.filter(c => inRange(c.created_at) && c.lead_status),
      quotesSent: allClients.filter(c => c.lead_status === 'Quote Sent' || c.lead_status === 'Awaiting Client'),
      policiesBound: (coverConfirmations || []).filter(cc => inRange(cc.created_at)).map(cc => {
        const d = cc.cover_data || {}
        const cl = allClients.find(c => c.id === cc.client_id) || {}
        return { id: cc.id, clientName: cl.company_name || d.insured_name || 'Unknown', coverType: d.cover_type || '—', insurer: d.insurer || '—', revenue: calcNetRev(d), broker: cc.created_by || '—' }
      }),
      activeLeads: allClients.filter(c => c.lead_status && c.lead_status !== 'Policy Placed' && c.lead_status !== 'Not Interested'),
      agedDebtors: (coverConfirmations || []).filter(cc => {
        const d = cc.cover_data || {}
        const pt = (d.payment_type || '').toLowerCase()
        const startDate = d.period_from ? new Date(d.period_from) : null
        if (!startDate) return false
        const daysOld = Math.floor((new Date() - startDate) / 86400000)
        return (pt !== 'monthly' && pt !== 'funded') && !d._remittance_paid && !d._paid_manually && daysOld >= 7
      }).map(cc => {
        const d = cc.cover_data || {}
        const cl = allClients.find(c => c.id === cc.client_id) || {}
        return { clientName: cl.company_name || 'Unknown', policyNumber: d.policy_number || '—', daysOld: Math.floor((new Date() - new Date(d.period_from)) / 86400000) }
      }),
    }
  }, [clients, coverConfirmations, statsDateRange])

  // ── Lead source breakdown ──
const { leadsBySource, leadsByPartner } = useMemo(() => {
    const sources = {}
    const partners = {}
const { start, end } = statsDateRange
    const inRange = (d) => { if (!d) return false; const dt = new Date(d); return dt >= start && dt <= end }
    const allLeads = (clients || []).filter(c => c.lead_status && c.created_at && (statsRange === 'all' || inRange(c.created_at)))
    allLeads.forEach(c => {
      // Partner breakdown
      if (c.referring_partner) {
        const pName = c.referring_partner
        if (!partners[pName]) partners[pName] = { total: 0, quoted: 0, bound: 0 }
        partners[pName].total++
        if (['Quote Sent', 'Awaiting Client', 'Policy Placed'].includes(c.lead_status)) partners[pName].quoted++
        if (c.lead_status === 'Policy Placed') partners[pName].bound++
      }

      // Source breakdown (clean categories only, no individual partners)
      let source = c.lead_source || 'Other'
      if (source === 'Referral Partner') source = 'Referral Partners (combined)'
      if (source === 'Other' && c.lead_source_other) source = `Other: ${c.lead_source_other}`

      if (!sources[source]) sources[source] = { total: 0, quoted: 0, bound: 0 }
      sources[source].total++
      if (['Quote Sent', 'Awaiting Client', 'Policy Placed'].includes(c.lead_status)) sources[source].quoted++
      if (c.lead_status === 'Policy Placed') sources[source].bound++
    })

    const sortedSources = Object.entries(sources).sort((a, b) => b[1].bound - a[1].bound || b[1].total - a[1].total)
    const sortedPartners = Object.entries(partners).sort((a, b) => b[1].bound - a[1].bound || b[1].total - a[1].total)
return { leadsBySource: Object.fromEntries(sortedSources), leadsByPartner: Object.fromEntries(sortedPartners) }
  }, [clients, statsDateRange, statsRange])
  // ── Per-broker breakdown ──
const leadsByBroker = useMemo(() => {
    const { start, end } = statsDateRange
    const inRange = (d) => { if (!d) return false; const dt = new Date(d); return dt >= start && dt <= end }
    const brokers = {}
    BROKERS.forEach(b => { brokers[b] = { leads: 0, quoted: 0, bound: 0, revenue: 0 } })
;(clients || []).filter(c => c.lead_status && c.created_at && inRange(c.created_at)).forEach(c => {
      const b = c.assigned_broker
      if (!brokers[b]) return
      brokers[b].leads++
      if (['Quote Sent', 'Awaiting Client', 'Policy Placed'].includes(c.lead_status)) brokers[b].quoted++
    })
    ;(coverConfirmations || []).filter(cc => inRange(cc.created_at)).forEach(cc => {
      const b = cc.created_by
      if (brokers[b]) {
        brokers[b].bound++
        brokers[b].revenue += calcNetRev(cc.cover_data || {})
      }
    })
    return brokers
  }, [clients, coverConfirmations, statsDateRange])

  // ── Partner stats ──
  const partnerStats = useMemo(() => {
    const now = new Date()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const referralLeads = (clients || []).filter(c => c.referring_partner)
    const thisMonthReferrals = referralLeads.filter(c => new Date(c.created_at) >= monthStart)
    const converted = referralLeads.filter(c => c.lead_status === 'Policy Placed')
    const partners = referralPartners || []
    const activePartners = [...new Set(referralLeads.filter(c => {
      const d = new Date(c.created_at)
      const threeMonthsAgo = new Date()
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
      return d >= threeMonthsAgo
    }).map(c => c.referring_partner))].length
    const stale = partners.length - activePartners

    return {
      total: partners.length,
      active: activePartners,
      thisMonth: thisMonthReferrals.length,
      converted: converted.length,
      stale: Math.max(0, stale),
    }
  }, [clients, referralPartners])

  // ── Operations ──
  const operations = useMemo(() => {
    const quoteLeads = (clients || []).filter(c => {
      if (c.lead_status !== 'Quote Sent') return false
      const d = new Date(c.updated_at || c.created_at)
      return (new Date() - d) > 48 * 60 * 60 * 1000
    })
    const openCl = (claims || []).filter(c => !['Settled', 'Withdrawn', 'Declined'].includes(c.status))
    const overdueCl = openCl.filter(c => {
      const days = Math.floor((new Date() - new Date(c.last_updated_at)) / 86400000)
      return days >= 10
    })
    const urgentDebtors = (coverConfirmations || []).filter(cc => {
      const d = cc.cover_data || {}
      const pt = (d.payment_type || '').toLowerCase()
      const startDate = d.period_from ? new Date(d.period_from) : null
      if (!startDate) return false
      const daysOld = Math.floor((new Date() - startDate) / 86400000)
      return (pt !== 'monthly' && pt !== 'funded') && !d._remittance_paid && !d._paid_manually && daysOld >= 7
    })
    return {
      agedQuotes: quoteLeads.length,
      openClaims: openCl.length,
      overdueClaims: overdueCl.length,
      urgentDebtors: urgentDebtors.length,
    }
  }, [clients, claims, coverConfirmations])

  // ── Save meeting ──
  const saveMeeting = async () => {
    setSaving(true)
    const payload = {
      week_start: weekStartStr,
      commitments,
      strategic_topic: strategicTopic || null,
      strategic_decision: strategicDecision || null,
      notes: notes || null,
      created_by: user?.name || 'Unknown',
      updated_at: new Date().toISOString(),
    }
    if (meeting) {
      await supabase.from('weekly_meetings').update(payload).eq('id', meeting.id)
    } else {
      const { data } = await supabase.from('weekly_meetings').insert(payload).select().single()
      setMeeting(data)
    }
    showToast('Meeting saved')
    setSaving(false)
  }

  // ── Commitment helpers ──
  const addCommitment = (broker) => {
    const text = (newCommitText[broker] || '').trim()
    if (!text) return
    setCommitments(prev => [...prev, { broker, text, done: false }])
    setNewCommitText(prev => ({ ...prev, [broker]: '' }))
  }

  const toggleCommitment = (idx) => {
    setCommitments(prev => prev.map((c, i) => i === idx ? { ...c, done: !c.done } : c))
  }

  const removeCommitment = (idx) => {
    setCommitments(prev => prev.filter((_, i) => i !== idx))
  }

  // ── Print ──
  const handlePrint = () => {
    printMeeting({
      weekStart: currentMonday,
      scoreboard,
      leadsBySource,
      leadsByBroker,
      partnerStats,
      operations,
      commitments,
      prevCommitments: prevMeeting?.commitments || [],
      strategic: { topic: strategicTopic, decision: strategicDecision },
      notes,
    })
  }

  const prevCommitments = prevMeeting?.commitments || []
  const isCurrentWeek = weekOffset === 0

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading meeting data...</span></div>

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Weekly Leadership Meeting</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{weekLabel(currentMonday)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setWeekOffset(w => w - 1)}>← Prev</button>
          {weekOffset !== 0 && <button className="btn btn-secondary btn-sm" onClick={() => setWeekOffset(0)}>This Week</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => setWeekOffset(w => w + 1)}>Next →</button>
          <button className="btn btn-secondary btn-sm" onClick={handlePrint} style={{ marginLeft: 8 }}>🖨 Print PDF</button>
          <button className="btn btn-primary btn-sm" onClick={saveMeeting} disabled={saving}>{saving ? 'Saving...' : '💾 Save Meeting'}</button>
        </div>
      </div>

      {/* ── 1. SCOREBOARD ── */}
      <div style={{ marginBottom: 24 }}>
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>1. Scoreboard</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {[['week','This Week'],['month','This Month'],['year','This Year'],['all','All Time']].map(([key,label]) => (
              <button key={key} onClick={() => setStatsRange(key)} style={{ padding: '4px 10px', borderRadius: 20, border: `1.5px solid ${statsRange === key ? 'var(--forest)' : 'var(--border)'}`, background: statsRange === key ? 'var(--forest)' : 'var(--surface2)', color: statsRange === key ? '#fff' : 'var(--muted)', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>{label}</button>
            ))}
            <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 8 }}>10 minutes</span>
          </div>
        </div>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
          {[
            { key: 'newLeads', label: `New Leads ${statsRange === 'week' ? 'This Week' : statsRange === 'month' ? 'This Month' : statsRange === 'year' ? 'This Year' : 'All Time'}`, value: scoreboard.newLeads, color: 'var(--text)' },
            { key: 'quotesSent', label: 'Quotes Outstanding', value: scoreboard.outstandingQuotes, color: scoreboard.outstandingQuotes > 5 ? '#7a5500' : 'var(--text)' },
            { key: 'policiesBound', label: 'Policies Bound', value: scoreboard.policiesBound, color: 'var(--forest)' },
            { key: null, label: `Revenue ${statsRange === 'week' ? 'This Week' : statsRange === 'month' ? 'This Month' : statsRange === 'year' ? 'This Year' : 'All Time'}`, value: fmtCurr(scoreboard.weekRevenue), color: 'var(--forest)' },
          ].map(item => (
            <div key={item.label} onClick={() => item.key && setDrilldown(drilldown === item.key ? null : item.key)} style={{ background: drilldown === item.key ? 'var(--forest-subtle)' : 'var(--surface)', border: `1.5px solid ${drilldown === item.key ? 'var(--forest-border)' : 'var(--border)'}`, borderRadius: 12, padding: '14px 16px', cursor: item.key ? 'pointer' : 'default', transition: 'all .15s' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{item.label}{item.key ? ' ▾' : ''}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
          {[
            { key: 'activeLeads', label: 'Active Leads', value: scoreboard.activeLeads, color: 'var(--text)' },
            { key: 'agedDebtors', label: 'Aged Debtors (7+ days)', value: scoreboard.agedDebtors, color: scoreboard.agedDebtors > 0 ? 'var(--danger)' : 'var(--forest)' },
            { key: null, label: 'Open Claims', value: scoreboard.openClaims, color: 'var(--text)' },
            { key: null, label: 'Conversion', value: `${scoreboard.activeLeads > 0 ? Math.round((scoreboard.policiesBound / Math.max(1, scoreboard.activeLeads + scoreboard.policiesBound)) * 100) : 0}%`, color: 'var(--text)' },
          ].map(item => (
            <div key={item.label} onClick={() => item.key && setDrilldown(drilldown === item.key ? null : item.key)} style={{ background: drilldown === item.key ? 'var(--forest-subtle)' : 'var(--surface)', border: `1.5px solid ${drilldown === item.key ? 'var(--forest-border)' : 'var(--border)'}`, borderRadius: 12, padding: '14px 16px', cursor: item.key ? 'pointer' : 'default', transition: 'all .15s' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{item.label}{item.key ? ' ▾' : ''}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>

        {drilldown && (
          <div style={{ background: 'var(--surface)', border: '1.5px solid var(--forest-border)', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ padding: '10px 16px', background: 'var(--forest-subtle)', borderBottom: '1.5px solid var(--forest-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)' }}>
                {drilldown === 'newLeads' ? 'New Leads' : drilldown === 'quotesSent' ? 'Quotes Outstanding' : drilldown === 'policiesBound' ? 'Policies Bound' : drilldown === 'activeLeads' ? 'Active Leads' : 'Aged Debtors'} — {(drilldownData[drilldown] || []).length} records
              </div>
              <button onClick={() => setDrilldown(null)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            {(drilldown === 'newLeads' || drilldown === 'activeLeads' || drilldown === 'quotesSent') && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '6px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {['Client', 'Status', 'Source', 'Broker', 'Added'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                </div>
                {(drilldownData[drilldown] || []).map(c => (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{c.company_name}</div>
                    <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--surface2)', color: 'var(--muted)' }}>{c.lead_status}</span></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.lead_source || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.assigned_broker?.split(' ')[0] || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}</div>
                  </div>
                ))}
                {(drilldownData[drilldown] || []).length === 0 && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>None</div>}
              </>
            )}
            {drilldown === 'policiesBound' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '6px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {['Client', 'Cover', 'Insurer', 'Broker', 'Revenue'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                </div>
                {(drilldownData.policiesBound || []).map((p, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{p.clientName}</div>
                    <div style={{ fontSize: 11 }}>{p.coverType}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.insurer}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{p.broker?.split(' ')[0] || '—'}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurr(p.revenue)}</div>
                  </div>
                ))}
                {(drilldownData.policiesBound || []).length === 0 && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>None</div>}
              </>
            )}
            {drilldown === 'agedDebtors' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, padding: '6px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {['Client', 'Policy', 'Days Overdue'].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>)}
                </div>
                {(drilldownData.agedDebtors || []).map((d, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{d.clientName}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{d.policyNumber}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>{d.daysOld}d</div>
                  </div>
                ))}
                {(drilldownData.agedDebtors || []).length === 0 && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>None</div>}
              </>
            )}
          </div>
        )}

        {/* Per-broker table */}
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '8px 16px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
            {['Broker', 'Leads', 'Quoted', 'Bound', 'Revenue'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>
            ))}
          </div>
{BROKERS.map(b => {
            const d = leadsByBroker[b] || {}
            return (
              <div key={b} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {BROKER_PHOTOS[b] && <img src={BROKER_PHOTOS[b]} alt={b} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid var(--forest-border)' }} />}
                  {b}
                </div>
                <div style={{ fontSize: 13 }}>{d.leads || 0}</div>
                <div style={{ fontSize: 13 }}>{d.quoted || 0}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>{d.bound || 0}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>{fmtCurr(d.revenue || 0)}</div>
              </div>
            )
          })}
        </div>
      </div>

{/* ── 2. LEAD GENERATION ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>2. Lead Generation — Sources</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>15 minutes</div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '8px 16px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
            {['Source', 'Leads', 'Quoted', 'Bound', 'Conversion'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>
            ))}
          </div>
          {Object.entries(leadsBySource).length === 0
            ? <div style={{ padding: 16, fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>No lead source data — add a lead_source field to leads for tracking</div>
            : Object.entries(leadsBySource).map(([source, d]) => (
              <div key={source} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{source}</div>
                <div style={{ fontSize: 13 }}>{d.total}</div>
                <div style={{ fontSize: 13 }}>{d.quoted}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>{d.bound}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: d.total > 0 && d.bound / d.total >= 0.3 ? 'var(--forest)' : d.total > 5 && d.bound === 0 ? 'var(--danger)' : 'var(--text)' }}>
                  {d.total > 0 ? Math.round((d.bound / d.total) * 100) : 0}%
                </div>
              </div>
            ))
          }
        </div>
      </div>

     <div style={{ background: 'rgba(30,61,48,0.04)', border: '1.5px solid var(--forest-border)', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)', marginBottom: 8 }}>💬 Discussion Questions — Lead Generation</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            'Which lead source generated the most revenue this week — not just the most leads?',
            'Are we following up every FSO/QuoteRack lead within 2 hours?',
            'Which source is consuming time but not converting? Should we cut it?',
            'What is the one thing we could do this week to generate 3 more quality leads?',
          ].map((q, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--forest)', fontWeight: 700, marginRight: 6 }}>{i + 1}.</span>{q}
            </div>
          ))}
        </div>
      </div>

      {/* ── Referral Partner Breakdown ── */}
      {Object.keys(leadsByPartner).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Referral Partner Performance</div>
          <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '8px 16px', background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
              {['Partner', 'Leads', 'Quoted', 'Bound', 'Conversion'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{h}</div>
              ))}
            </div>
            {Object.entries(leadsByPartner).map(([partner, d]) => {
              const convPct = d.total > 0 ? Math.round((d.bound / d.total) * 100) : 0
              return (
                <div key={partner} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'rgba(242,101,34,0.12)', color: 'var(--orange)', fontWeight: 700 }}>🤝</span>
                    {partner}
                  </div>
                  <div style={{ fontSize: 13 }}>{d.total}</div>
                  <div style={{ fontSize: 13 }}>{d.quoted}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: d.bound > 0 ? 'var(--forest)' : 'var(--muted)' }}>{d.bound}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: convPct >= 50 ? 'var(--forest)' : convPct > 0 ? 'var(--text)' : 'var(--danger)' }}>{convPct}%</div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {Object.keys(leadsByPartner).length > 0 && (
        <div style={{ background: 'rgba(242,101,34,0.04)', border: '1.5px solid rgba(242,101,34,0.2)', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', marginBottom: 8 }}>💬 Discussion Questions — Referral Partners</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              'Which partner sent the best quality leads this week — not just volume?',
              'Which partners haven\'t sent anything in 30+ days? Who is calling them this week?',
              'Did we send a thank-you or update to every partner who referred this week?',
              'Who are 3 new finance brokers or accountants we should approach this week?',
            ].map((q, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid rgba(242,101,34,0.15)', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--orange)', fontWeight: 700, marginRight: 6 }}>{i + 1}.</span>{q}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 3. REFERRAL PARTNER GROWTH ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>3. Referral Partner Growth</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>10 minutes</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {[
            { label: 'Active Partners', value: partnerStats.active, color: 'var(--forest)' },
            { label: 'Referrals This Month', value: partnerStats.thisMonth, color: 'var(--text)' },
            { label: 'Referrals Converted', value: partnerStats.converted, color: 'var(--forest)' },
            { label: 'Partners Needing Follow-Up', value: partnerStats.stale, color: partnerStats.stale > 0 ? '#7a5500' : 'var(--forest)' },
          ].map(item => (
            <div key={item.label} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>{item.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'rgba(30,61,48,0.04)', border: '1.5px solid var(--forest-border)', borderRadius: 10, padding: '12px 18px', marginTop: 12, marginBottom: 24, display: 'flex', gap: 24, alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--forest)' }}>🎯 Targets</div>
        <div style={{ fontSize: 12, color: 'var(--text)' }}>1 new referral meeting per director per week</div>
        <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
        <div style={{ fontSize: 12, color: 'var(--text)' }}>1 new partner activated per month</div>
        <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
        <div style={{ fontSize: 12, color: 'var(--text)' }}>Every partner gets a thank-you within 24 hours of a referral</div>
      </div>

      {/* ── 4. OPERATIONS & SERVICE ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>4. Operations & Service</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>10 minutes</div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          {[
            { label: 'Quotes older than 48 hours', value: operations.agedQuotes, status: operations.agedQuotes > 0 ? 'Follow up today' : 'All clear', color: operations.agedQuotes > 0 ? '#7a5500' : 'var(--forest)' },
            { label: 'Open Claims', value: operations.openClaims, status: operations.openClaims > 0 ? 'Review status' : 'None', color: 'var(--text)' },
            { label: 'Claims overdue update (10+ days)', value: operations.overdueClaims, status: operations.overdueClaims > 0 ? 'Update client today' : 'All current', color: operations.overdueClaims > 0 ? 'var(--danger)' : 'var(--forest)' },
            { label: 'BPAY Debtors (7+ days)', value: operations.urgentDebtors, status: operations.urgentDebtors > 0 ? 'Send follow-up SMS' : 'None', color: operations.urgentDebtors > 0 ? 'var(--danger)' : 'var(--forest)' },
          ].map(item => (
            <div key={item.label} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1.5fr', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div style={{ fontSize: 13 }}>{item.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</div>
              <div style={{ fontSize: 12, color: item.color, fontWeight: 600 }}>{item.status}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: 'rgba(220,53,69,0.04)', border: '1.5px solid rgba(220,53,69,0.15)', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)', marginBottom: 8 }}>💬 Discussion Questions — Operations</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            'What is the one thing that frustrated a client or slowed us down this week?',
            'Are there any compliance items overdue — fact finds, TMDs, informed consent?',
            'Is every quote being followed up within 48 hours? If not, why?',
            'What is one process we can fix or automate this week to save 30 minutes a day?',
          ].map((q, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 8, border: '1px solid rgba(220,53,69,0.1)', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--danger)', fontWeight: 700, marginRight: 6 }}>{i + 1}.</span>{q}
            </div>
          ))}
        </div>
      </div>

      {/* ── 5. STRATEGIC DISCUSSION ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>5. Strategic Discussion</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>10 minutes · ONE topic only</div>
        </div>
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '16px 20px' }}>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">This Week's Topic</label>
            <input className="form-input" value={strategicTopic} onChange={e => setStrategicTopic(e.target.value)} placeholder="e.g. Should we spend $2k/month on Google Ads?" />
          </div>
          <div>
            <label className="form-label">Decision / Outcome</label>
            <input className="form-input" value={strategicDecision} onChange={e => setStrategicDecision(e.target.value)} placeholder="e.g. Yes — starting July 1, $1.5k budget, Ben to set up" />
          </div>
        </div>
      </div>

      {/* ── 6. COMMITMENTS ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>6. Commitments</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>5 minutes · 3 actions each · Green = done · Red = not done</div>
        </div>

        {/* Last week's commitments */}
        {prevCommitments.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Last Week's Commitments</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
              {BROKERS.map(b => {
                const items = prevCommitments.filter(c => c.broker === b)
                return (
                  <div key={b} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)', marginBottom: 8 }}>{b.split(' ')[0]}</div>
                    {items.length === 0
                      ? <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>None recorded</div>
                      : items.map((c, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 0', fontSize: 12 }}>
                          <span style={{ fontSize: 14 }}>{c.done ? '✅' : '❌'}</span>
                          <span style={{ color: c.done ? 'var(--text)' : 'var(--danger)', textDecoration: c.done ? 'none' : 'none' }}>{c.text}</span>
                        </div>
                      ))
                    }
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* This week's commitments */}
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>This Week's Commitments</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {BROKERS.map(b => {
            const items = commitments.filter(c => c.broker === b)
            return (
              <div key={b} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
<div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {BROKER_PHOTOS[b] && <img src={BROKER_PHOTOS[b]} alt={b} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />}
                  {b.split(' ')[0]}
                </div>
                {items.map((c, idx) => {
                  const globalIdx = commitments.indexOf(c)
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 12 }}>
                      <input type="checkbox" checked={c.done} onChange={() => toggleCommitment(globalIdx)} style={{ accentColor: 'var(--forest)', cursor: 'pointer' }} />
                      <span style={{ flex: 1, textDecoration: c.done ? 'line-through' : 'none', color: c.done ? 'var(--muted)' : 'var(--text)' }}>{c.text}</span>
                      <button onClick={() => removeCommitment(globalIdx)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, padding: '0 4px' }}>✕</button>
                    </div>
                  )
                })}
                {items.length < 3 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input
                      className="form-input"
                      value={newCommitText[b] || ''}
                      onChange={e => setNewCommitText(prev => ({ ...prev, [b]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') addCommitment(b) }}
                      placeholder={`Add action for ${b.split(' ')[0]}...`}
                      style={{ fontSize: 12, padding: '5px 8px', flex: 1 }}
                    />
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => addCommitment(b)}>+</button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── NOTES ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)', marginBottom: 10 }}>Meeting Notes</div>
        <textarea
          className="form-input"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={4}
          placeholder="Key discussion points, decisions, follow-ups..."
          style={{ fontSize: 13, lineHeight: 1.7 }}
        />
      </div>

      {/* ── CLOSING QUESTION ── */}
      <div style={{ background: 'linear-gradient(135deg,rgba(30,61,48,0.06),rgba(30,61,48,0.02))', border: '1.5px solid var(--forest-border)', borderRadius: 14, padding: '20px 24px', textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--forest)', lineHeight: 1.6 }}>
          "What activities last week directly created opportunities to write more premium next week?"
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>If the meeting stays focused on this, you'll avoid startup noise.</div>
      </div>

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn btn-secondary" onClick={handlePrint}>🖨 Print Meeting Pack</button>
        <button className="btn btn-primary" onClick={saveMeeting} disabled={saving}>{saving ? 'Saving...' : '💾 Save Meeting'}</button>
      </div>
    </div>
  )
}
